import React from 'react';

import { useAuthentication } from '@context/AuthContext';
import { useOnboardingStatus } from '@context/UsersContext';
import { listenToNotifications } from '@db/Notifications';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { sortBy } from '@helpers/Formatters';
import { logError } from '@helpers/Loggers';
import { requestNotificationsPermissions, syncUserNotificationToken } from '@helpers/Notifications';
import { Notification } from '@types';

export type NotificationsContextProps = {
  notifications: Notification[];
  unreadNotificationCount: number;
  /** Request notification access, then synchronize the current device push token. */
  requestPermissionAndSync: () => Promise<void>;
};

export const NotificationsContext = createNamedContext<NotificationsContextProps>({
  NotificationsContext: undefined,
});

export function useNotifications() {
  return useRequiredContext(NotificationsContext);
}

export function NotificationsProvider({ children }: React.PropsWithChildren) {
  const { loggedIn } = useAuthentication();
  const { onboarded } = useOnboardingStatus();
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const unreadNotificationCount = React.useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );

  const requestPermissionAndSync = React.useCallback(async () => {
    await requestNotificationsPermissions();
    await syncUserNotificationToken();
  }, []);

  React.useEffect(() => {
    if (loggedIn)
      // TODO: limit the number of notifications
      // but need to be careful about unread notifications
      return listenToNotifications(
        (notifications) => setNotifications(sortBy(notifications, 'createdAt', 'desc')),
        (error) => logError(error)
      );
  }, [loggedIn]);

  React.useEffect(() => {
    if (onboarded) {
      syncUserNotificationToken().catch((error) => logError(error));
    }
  }, [onboarded]);

  const value = { notifications, unreadNotificationCount, requestPermissionAndSync };

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
